/**
 * ContactWidgetPage — compact GHL contact record sidebar embed.
 *
 * URL: /app/contact/:contactId?ssoToken={token}
 *
 * Layout:
 *   ┌─────────────────────────────────┐
 *   │  🔒 ClearIntake                 │
 *   ├─────────────────────────────────┤
 *   │  Secure Messages (2 unread)     │
 *   │  • "Your lab results…" — 2d ago │
 *   │  • "Appointment reminder" — 1w  │
 *   │  [View All Messages →]          │
 *   ├─────────────────────────────────┤
 *   │  Forms                          │
 *   │  • Health History — Completed ✅│
 *   │  • HIPAA Consent — Pending ⏳   │
 *   │  [Send New Form →]              │
 *   └─────────────────────────────────┘
 *
 * Calls:
 *   GET /api/messages?contactId={id}&limit=3
 *   GET /api/forms/assignments?contactId={id}&limit=5
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../lib/auth-context.js';
import {
  messages as messagesApi,
  forms as formsApi,
  packageRuns as packageRunsApi,
  MessageSummary,
  FormAssignment,
  PackageRunSummary,
} from '../../../lib/api.js';
import { notifyParentHeight } from '../../../lib/iframe-resize.js';

export default function ContactWidgetPage(): React.ReactElement {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, isStaff } = useAuth();

  const [messages,     setMessages]     = useState<MessageSummary[]>([]);
  const [assignments,  setAssignments]  = useState<FormAssignment[]>([]);
  const [runs,         setRuns]         = useState<PackageRunSummary[]>([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [dataLoading,  setDataLoading]  = useState(true);
  const [error,        setError]        = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff || !contactId) return;

    const fetchData = async (): Promise<void> => {
      setDataLoading(true);
      setError(null);
      try {
        const [msgRes, assignRes, runsRes] = await Promise.all([
          messagesApi.list({ contactId, limit: 3 }),
          formsApi.assignments.list({ contactId, limit: 5 }),
          packageRunsApi.listForStaff({ contactId, limit: 5 }),
        ]);

        if (msgRes.success && msgRes.data) {
          setMessages(msgRes.data.messages);
          setUnreadCount(msgRes.data.unreadCount);
        }

        if (assignRes.success && assignRes.data) {
          setAssignments(assignRes.data.assignments);
        }

        if (runsRes.success && runsRes.data) {
          setRuns(runsRes.data.runs);
        }
      } catch {
        setError('Failed to load contact data');
      } finally {
        setDataLoading(false);
      }
    };

    void fetchData();
  }, [isStaff, contactId]);

  // Notify GHL parent of our height after render
  useEffect(() => {
    notifyParentHeight(document.body.scrollHeight);
  });

  if (authLoading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}><span style={styles.headerTitle}>🔒 ClearIntake</span></div>
        <div style={styles.loading}><p style={styles.muted}>Connecting…</p></div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}><span style={styles.headerTitle}>🔒 ClearIntake</span></div>
        <div style={styles.loading}><p style={styles.muted}>Open from your seeUwell menu to view.</p></div>
      </div>
    );
  }

  if (!contactId) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}><span style={styles.headerTitle}>🔒 ClearIntake</span></div>
        <div style={styles.loading}><p style={{ color: '#ef4444', fontSize: 13 }}>No contact ID provided.</p></div>
      </div>
    );
  }

  return (
    <div style={styles.widget}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.headerTitle}>🔒 ClearIntake</span>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {dataLoading ? (
        <div style={styles.loading}><p style={styles.muted}>Loading…</p></div>
      ) : (
        <>
          {/* Messages section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>
                Secure Messages
                {unreadCount > 0 && <span style={styles.badge}>{unreadCount} unread</span>}
              </span>
            </div>

            {messages.length === 0 ? (
              <p style={styles.empty}>No messages yet</p>
            ) : (
              <div style={styles.list}>
                {messages.map((m) => (
                  <div key={m.id} style={styles.listItem}>
                    <span style={styles.itemLabel}>
                      {!m.readAt && <span style={styles.unreadDot} />}
                      {m.subject.length > 35 ? m.subject.slice(0, 35) + '…' : m.subject}
                    </span>
                    <span style={styles.itemMeta}>{relativeTime(m.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              style={styles.actionBtn}
              onClick={() => navigate(`/app/mailbox?contactId=${contactId}`)}
            >
              View All Messages →
            </button>
          </div>

          {/* Forms section */}
          <div style={{ ...styles.section, borderTop: '1px solid #e5e7eb' }}>
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>Forms</span>
            </div>

            {assignments.length === 0 ? (
              <p style={styles.empty}>No forms assigned</p>
            ) : (
              <div style={styles.list}>
                {assignments.map((a) => (
                  <div key={a.id} style={styles.listItem}>
                    <span style={styles.itemLabel}>{a.formName}</span>
                    <span style={{
                      ...styles.statusBadge,
                      ...(a.status === 'completed' ? styles.statusComplete :
                          a.status === 'expired'   ? styles.statusExpired  :
                          styles.statusPending),
                    }}>
                      {a.status === 'completed' ? '✅ Completed' :
                       a.status === 'expired'   ? '⏰ Expired'   :
                       '⏳ Pending'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              style={styles.actionBtn}
              onClick={() => navigate(`/app/forms?contactId=${contactId}`)}
            >
              Send New Form →
            </button>
          </div>

          {/* Form packages section */}
          <div style={{ ...styles.section, borderTop: '1px solid #e5e7eb' }}>
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>Form packages</span>
            </div>

            {runs.length === 0 ? (
              <p style={styles.empty}>No packages sent</p>
            ) : (
              <div style={styles.list}>
                {runs.map((r) => (
                  <div key={r.id} style={styles.listItem}>
                    <span style={styles.itemLabel}>{r.packageName}</span>
                    <span style={{
                      ...styles.statusBadge,
                      ...(r.status === 'completed' ? styles.statusComplete :
                          r.status === 'expired'   ? styles.statusExpired  :
                          styles.statusPending),
                    }}>
                      {r.status === 'completed' ? '✅' :
                       r.status === 'expired'   ? '⏰' :
                       '⏳'}{' '}{r.itemsCompleted}/{r.itemsTotal}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60)   return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)     return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30)      return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8)      return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  widget: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: '#fff',
    border:     '1px solid #e5e7eb',
    borderRadius: 8,
    overflow:   'hidden',
    fontSize:   13,
  },
  header: {
    background:   '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding:      '10px 14px',
    display:      'flex',
    alignItems:   'center',
  },
  headerTitle: { color: '#111827', fontWeight: 700, fontSize: 14 },
  loading: { padding: '16px 14px', textAlign: 'center' },
  muted: { color: '#9ca3af', fontSize: 13 },
  errorBanner: {
    background: '#fef2f2', borderBottom: '1px solid #fca5a5',
    padding: '8px 14px', color: '#b91c1c', fontSize: 12,
  },
  section: { padding: '12px 14px' },
  sectionHeader: { marginBottom: 8 },
  sectionTitle: { fontWeight: 700, color: '#111827', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  badge: {
    background: '#ef4444', color: '#fff', borderRadius: 10,
    padding: '1px 6px', fontSize: 10, fontWeight: 700,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 },
  listItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  itemLabel: {
    color: '#374151', flex: 1, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  itemMeta: { color: '#9ca3af', fontSize: 11, flexShrink: 0 },
  unreadDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#3b82f6', flexShrink: 0,
    display: 'inline-block',
  },
  statusBadge: { fontSize: 11, fontWeight: 600, flexShrink: 0 },
  statusComplete: { color: '#059669' },
  statusPending:  { color: '#d97706' },
  statusExpired:  { color: '#9ca3af' },
  empty: { color: '#9ca3af', fontSize: 12, marginBottom: 10 },
  actionBtn: {
    background: 'none', border: '1px solid #d1d5db',
    borderRadius: 5, padding: '5px 10px',
    cursor: 'pointer', fontSize: 12, color: '#2563eb',
    fontWeight: 500, width: '100%', textAlign: 'left',
  },
};
